/**
 * Reports — date-range filter form.
 *
 * Phase 21.F migration: replaces the IIFE that used to live at the
 * bottom of ``templates/office/reports/_filters.html`` and bound a
 * ``changeDate`` listener to the two date inputs (``#filter-start``
 * / ``#filter-end``) so that any manual date pick flips the hidden
 * ``#filter-preset`` input from one of the named presets (``today``,
 * ``last_7d``, ``mtd``, ``last_month``, ``ytd``…) to ``custom``.
 *
 * Why a dedicated module rather than extending ``pages/reports.js``
 * ----------------------------------------------------------------
 *
 * ``pages/reports.js::initReports()`` is only called when the
 * ``#report-chart-data`` ``json_script`` is present (= dashboard /
 * tab pages with charts). The filter bar is included on **every**
 * reports page (including the future export-only / table-only ones
 * that have no ApexCharts payload), so its bootstrap must be
 * independent. A separate auto-init is the cleanest way to keep
 * the chart pipeline (heavy, conditional) decoupled from the
 * filter UI (light, ubiquitous).
 *
 * Contract
 * --------
 *
 * - The filter ``<form id="report-filter-form">`` carries
 *   ``data-auto-init="reportFilters"``. ``app.js`` ``_runAutoInit``
 *   calls ``App.pages.autoInitReportFilters()`` on first load and
 *   on every ``htmx:afterSwap`` of ``#main-content``.
 *
 * - The ``changeDate`` event is fired by Flowbite/datepicker (the
 *   ``datepicker`` attribute on the ``<input>``) when the user
 *   picks a date from the calendar popup. We bind directly on the
 *   input (not delegated on ``document``) because the event is
 *   fired ON the input element and does NOT bubble in some
 *   datepicker versions.
 *
 * - Per-form ``data-rf-bound`` flag: re-running the auto-init on
 *   the same form (e.g. after a partial re-render of the filter
 *   bar) is a no-op. The form root is fully replaced on full HTMX
 *   swaps so we re-scan + re-bind on each call.
 */

const FORM_SELECTOR = '#report-filter-form';
const PRESET_INPUT_ID = 'filter-preset';
const START_INPUT_ID = 'filter-start';
const END_INPUT_ID = 'filter-end';
const CUSTOM_PRESET_VALUE = 'custom';

function flipToCustom(form) {
  const preset = form.querySelector('#' + PRESET_INPUT_ID);
  if (preset) preset.value = CUSTOM_PRESET_VALUE;
}

export function initReportFilters() {
  const form = document.querySelector(FORM_SELECTOR);
  if (!form) return;
  if (form.dataset.rfBound === '1') return;
  form.dataset.rfBound = '1';

  const start = form.querySelector('#' + START_INPUT_ID);
  const end = form.querySelector('#' + END_INPUT_ID);

  if (start) start.addEventListener('changeDate', () => flipToCustom(form));
  if (end) end.addEventListener('changeDate', () => flipToCustom(form));
}

export function autoInitReportFilters() {
  initReportFilters();
}
